import { httpClient } from '@/shared/api/httpClient';

export type TipoLancamento = 'Receber' | 'Pagar';
export type StatusLancamento = 'EmAberto' | 'Pago' | 'Cancelado';
export type TipoLancamentoItem = 'Produto' | 'Servico' | 'Outro';
export type TipoDescontoItem = 'Percentual' | 'Valor';

export type FormaPagamento =
  | 'Dinheiro'
  | 'Pix'
  | 'Cartao'
  | 'Boleto'
  | 'TransferenciaBancaria'
  | 'Cheque'
  | 'Outro';

export const FORMAS_PAGAMENTO: Array<{ id: FormaPagamento; label: string }> = [
  { id: 'Dinheiro', label: 'Dinheiro' },
  { id: 'Pix', label: 'Pix' },
  { id: 'Cartao', label: 'Cartão' },
  { id: 'Boleto', label: 'Boleto' },
  { id: 'TransferenciaBancaria', label: 'Transferência bancária' },
  { id: 'Cheque', label: 'Cheque' },
  { id: 'Outro', label: 'Outro' },
];

export type LancamentoResumo = {
  id: string;
  tipo: TipoLancamento;
  descricao: string;
  categoria: string | null;
  valor: number;
  valorPago: number;
  saldo: number;
  emissao: string;
  vencimento: string;
  pagamento: string | null;
  formaPagamento: FormaPagamento | null;
  status: StatusLancamento;
  clienteId: string | null;
  clienteNome: string | null;
  fornecedor: string | null;
  orcamentoId: string | null;
  orcamentoNumero: string | null;
  observacoes: string | null;
  diasAtraso: number;
  quantidadePagamentos: number;
  quantidadeItens: number;
  /** true enquanto não foi confirmado pelo servidor. */
  syncPendente: boolean;
};

export type ItemLancamentoEntrada = {
  tipo: TipoLancamentoItem;
  referenciaId?: string | null;
  orcamentoItemId?: string | null;
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  descontoTipo?: TipoDescontoItem | null;
  descontoValor?: number | null;
};

export type ItemLancamentoDetalhe = ItemLancamentoEntrada & {
  id: string;
  ordem: number;
  subtotalBruto: number;
  valorDesconto: number;
  subtotal: number;
};

export type PagamentoDetalhe = {
  id: string;
  data: string;
  valor: number;
  formaPagamento: FormaPagamento | null;
  observacao: string | null;
  estornado: boolean;
  estornadoEm: string | null;
  estornadoMotivo: string | null;
  criadoEm: string;
};

export type LancamentoDetalhe = LancamentoResumo & {
  itens: ItemLancamentoDetalhe[];
  pagamentos: PagamentoDetalhe[];
};

export type ResumoFinanceiro = {
  totalAberto: number;
  totalPago: number;
  totalVencido: number;
  totalAVencer: number;
  qtdAberto: number;
  qtdPago: number;
  qtdVencido: number;
};

export type CriarLancamentoRequest = {
  tipo: TipoLancamento;
  descricao: string;
  categoria?: string | null;
  valor?: number | null;
  emissao?: string | null;
  vencimento: string;
  clienteId?: string | null;
  fornecedor?: string | null;
  orcamentoId?: string | null;
  observacoes?: string | null;
  itens?: ItemLancamentoEntrada[] | null;
};

export type AtualizarLancamentoRequest = {
  descricao: string;
  categoria?: string | null;
  valor?: number | null;
  vencimento: string;
  clienteId?: string | null;
  fornecedor?: string | null;
  observacoes?: string | null;
  itens?: ItemLancamentoEntrada[] | null;
};

export type BaixarLancamentoRequest = {
  valor: number;
  data?: string | null;
  formaPagamento?: FormaPagamento | null;
  observacao?: string | null;
};

export type BaixarLancamentoResponse = {
  pagamentoId: string;
  status: StatusLancamento;
  saldo: number;
};

function querystring(params: Record<string, string | undefined>) {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') usp.set(k, v);
  }
  const s = usp.toString();
  return s === '' ? '' : `?${s}`;
}

export type FiltrosFinanceiro = {
  tipo?: TipoLancamento;
  status?: StatusLancamento;
  inicio?: string;
  fim?: string;
  clienteId?: string;
  orcamentoId?: string;
  /** Categoria exata. O PDV marca os fechamentos de caixa com "PDV". */
  categoria?: string;
  busca?: string;
};

export const financeiroApi = {
  listar: (filtros: FiltrosFinanceiro) =>
    httpClient.get<LancamentoResumo[]>(`/api/financeiro${querystring(filtros)}`),
  obter: (id: string) => httpClient.get<LancamentoDetalhe>(`/api/financeiro/${id}`),
  listarCategorias: () => httpClient.get<string[]>('/api/financeiro/categorias'),
  resumo: (filtros: { tipo?: TipoLancamento; inicio?: string; fim?: string }) =>
    httpClient.get<ResumoFinanceiro>(`/api/financeiro/resumo${querystring(filtros)}`),
  itensDoOrcamento: (orcamentoId: string) =>
    httpClient.get<ItemLancamentoEntrada[]>(`/api/financeiro/itens-do-orcamento/${orcamentoId}`),
  criar: (cmd: CriarLancamentoRequest) =>
    httpClient.post<{ id: string }>('/api/financeiro', cmd),
  atualizar: (id: string, cmd: AtualizarLancamentoRequest) =>
    httpClient.put<void>(`/api/financeiro/${id}`, cmd),
  baixar: (id: string, cmd: BaixarLancamentoRequest) =>
    httpClient.post<BaixarLancamentoResponse>(`/api/financeiro/${id}/baixar`, cmd),
  estornarPagamento: (id: string, pagamentoId: string, motivo: string | null) =>
    httpClient.post<void>(`/api/financeiro/${id}/pagamentos/${pagamentoId}/estornar`, { motivo }),
  cancelar: (id: string) =>
    httpClient.post<void>(`/api/financeiro/${id}/cancelar`, {}),
  excluir: (id: string) => httpClient.delete<void>(`/api/financeiro/${id}`),
};
